/**
 * Desenho do Mapa: um ELEMENTO por setor cadastrado — a "sala".
 *
 * Mudança da v3: as salas não são mais cinco retângulos escritos no código com
 * posição salva num `/layout` próprio. Agora cada setor do cadastro traz a
 * própria FORMA (`dados/forma.js`), desenhada a mão pelo gestor, e o desenho
 * chega junto do resto do estado (`/estado.setores`). Este módulo só traduz a
 * lista de setores para os elementos que o mapa desenha, na ORDEM DO FLUXO:
 *
 *     {setorId, nome, etapa, etapaRotulo, recebeDaPlanilha, ordem, versao, forma}
 *
 * A ordem do fluxo é a da ETAPA na planilha (`dados/etapasPlanilha.js`) e, para
 * vários setores na mesma etapa, a `ordem` do cadastro. É essa sequência que
 * desenha os corredores e as portas (`planta.js`) — a fábrica é lida como
 * caminho, não como cartões soltos.
 *
 * Setor recém-criado (ou criado pelo atalho da bandeja, sem ninguém desenhar
 * nada) não fica sem lugar: recebe uma caixa automática abaixo do que já
 * existe. O gestor redesenha no Organizar quando quiser.
 */
import { ordemDoVinculo } from "./dados/etapasPlanilha";
import { caixaDeVarias, caixaEnvolvente, criarRetangulo, ehFormaValida, normalizar, redimensionar } from "./dados/forma";
import { rotuloDaEtapa } from "./setores";

/** Caixa de um setor sem desenho — grande o bastante para subsetores e fila. */
export const TAMANHO_PADRAO = Object.freeze({ largura: 680, altura: 440 });
export const ESPACO_PADRAO = 40;
export const MARGEM_PADRAO = 40;
const COLUNAS_PADRAO = 3;

const SEM_ORDEM = Number.MAX_SAFE_INTEGER;
const LISTA_VAZIA = Object.freeze([]);

// Sala ligada ao grupo inteiro entra na posição da primeira etapa dele.
const ordemDaEtapa = (codigo) => ordemDoVinculo(codigo) ?? SEM_ORDEM;

function compararFluxo(a, b) {
  return (
    ordemDaEtapa(a.etapa) - ordemDaEtapa(b.etapa) ||
    (a.ordem ?? 0) - (b.ordem ?? 0) ||
    String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { numeric: true })
  );
}

/**
 * Os setores na ordem em que o ambiente os percorre. Setor de etapa
 * desconhecida (código que saiu do catálogo) vai para o fim, em vez de
 * embaralhar o fluxo por causa de um vínculo velho.
 */
export function ordenarPeloFluxo(setores) {
  return [...(setores || [])].sort(compararFluxo);
}

/** Onde a próxima caixa automática começa: abaixo de tudo o que já tem forma. */
export function fundoDosElementos(elementos) {
  const caixas = (elementos || []).map((e) => caixaEnvolvente(e.forma || e)).filter(Boolean);
  if (caixas.length === 0) return MARGEM_PADRAO;
  return Math.max(...caixas.map((c) => c.y + c.altura)) + ESPACO_PADRAO;
}

/**
 * Caixa automática do `indice`-ésimo setor sem desenho, a partir de `base`
 * (o fundo do que já existe): três por linha, na ordem do fluxo.
 */
export function formaAutomatica(indice, base = MARGEM_PADRAO) {
  const coluna = indice % COLUNAS_PADRAO;
  const linha = Math.floor(indice / COLUNAS_PADRAO);
  return criarRetangulo({
    x: MARGEM_PADRAO + coluna * (TAMANHO_PADRAO.largura + ESPACO_PADRAO),
    y: base + linha * (TAMANHO_PADRAO.altura + ESPACO_PADRAO),
    largura: TAMANHO_PADRAO.largura,
    altura: TAMANHO_PADRAO.altura,
  });
}

/**
 * Forma para um setor novo (o atalho "Criar setor para <etapa>" da bandeja
 * cria sem desenho): a primeira caixa livre abaixo das salas atuais.
 */
export function formaParaNovoSetor(elementos) {
  return formaAutomatica(0, fundoDosElementos(elementos));
}

function elementoDoSetor(setor, forma) {
  return Object.freeze({
    setorId: setor.id,
    nome: setor.nome || "",
    etapa: setor.etapa || null,
    etapaRotulo: rotuloDaEtapa(setor.etapa),
    recebeDaPlanilha: Boolean(setor.recebe_da_planilha),
    ordem: setor.ordem ?? 0,
    versao: setor.versao ?? 0,
    forma,
  });
}

/**
 * Elementos a desenhar, na ordem do fluxo. `setores` = os ATIVOS do catálogo
 * (`criarCatalogoSetores(...).setores`). Sem setor cadastrado, lista vazia — e
 * a tela mostra o convite para desenhar a primeira sala, não um mapa falso.
 */
export function elementosParaDesenho(setores) {
  const ordenados = ordenarPeloFluxo(setores).filter((setor) => setor && setor.id);
  if (ordenados.length === 0) return LISTA_VAZIA;
  const comForma = ordenados.filter((setor) => ehFormaValida(setor.forma));
  const base = fundoDosElementos(comForma.map((setor) => ({ forma: setor.forma })));
  let semDesenho = 0;
  return ordenados.map((setor) => {
    if (ehFormaValida(setor.forma)) return elementoDoSetor(setor, normalizar(setor.forma));
    const forma = formaAutomatica(semDesenho, base);
    semDesenho += 1;
    return elementoDoSetor(setor, forma);
  });
}

/** Caixa que envolve todas as salas (para "ajustar à tela"). */
export function caixaDosElementos(elementos) {
  const caixa = caixaDeVarias((elementos || []).map((e) => e.forma || e));
  return caixa || { x: 0, y: 0, largura: 0, altura: 0 };
}

/** Caixa envolvente de um elemento (é onde a alça de redimensionar fica). */
export function caixaDoElemento(elemento) {
  return caixaEnvolvente(elemento?.forma || elemento);
}

/** Os mesmos elementos com a forma de um setor trocada (lista nova). */
export function comFormaDoSetor(elementos, setorId, forma) {
  const nova = normalizar(forma);
  if (!nova) return elementos;
  return (elementos || []).map((elemento) =>
    elemento.setorId === setorId ? Object.freeze({ ...elemento, forma: nova }) : elemento
  );
}

/**
 * O que mandar no `PUT /formas`: só os setores cuja forma mudou, cada um com a
 * versão que a tela carregou. Lista vazia = nada a salvar (o gestor entrou no
 * Organizar e saiu sem mexer).
 */
/**
 * Caixa do filho levada pela mesma transformação que levou a sala de `de`
 * para `para` (translação + escala). É o que mantém o subsetor no MESMO lugar
 * relativo quando a sala anda ou muda de tamanho.
 */
function caixaLevada(filho, de, para) {
  const fx = de.largura > 0 ? para.largura / de.largura : 1;
  const fy = de.altura > 0 ? para.altura / de.altura : 1;
  return {
    x: para.x + (filho.x - de.x) * fx,
    y: para.y + (filho.y - de.y) * fy,
    largura: filho.largura * fx,
    altura: filho.altura * fy,
  };
}

/**
 * Subsetores e postos DESENHADOS andam junto com a sala no Organizar. Sem
 * isto, mover a sala deixava o desenho de dentro para trás, e o backend
 * recusava salvar ("não cabe dentro do setor") — a sala ficava presa.
 *
 * Devolve `{estado, filhos}`: `estado` com as formas dos filhos já levadas
 * (é o que a planta desenha durante o arrasto) e `filhos` = linhas do
 * PUT /formas ({alvo, id, forma, versao}) para salvar junto com as salas.
 */
export function levarFilhos(estado, originais, elementos) {
  const antes = new Map((originais || []).map((e) => [e.setorId, e.forma]));
  const mudaram = new Map();
  (elementos || []).forEach((e) => {
    const velha = antes.get(e.setorId);
    if (velha && JSON.stringify(velha) !== JSON.stringify(e.forma)) {
      const de = caixaEnvolvente(velha);
      const para = caixaEnvolvente(e.forma);
      if (de && para) mudaram.set(e.setorId, { de, para });
    }
  });
  if (!mudaram.size || !estado) return { estado, filhos: [] };
  const filhos = [];
  const levar = (alvo) => (item) => {
    const salaMexida = mudaram.get(item.setor_id);
    const forma = salaMexida ? normalizar(item.forma) : null;
    if (!forma) return item;
    const nova = redimensionar(forma, caixaLevada(caixaEnvolvente(forma), salaMexida.de, salaMexida.para));
    if (!nova) return item;
    filhos.push({ alvo, id: item.id, forma: nova, versao: item.versao });
    return { ...item, forma: nova };
  };
  return {
    estado: {
      ...estado,
      subsetores: (estado.subsetores || []).map(levar("subsetor")),
      postos: (estado.postos || []).map(levar("posto")),
    },
    filhos,
  };
}

export function formasAlteradas(elementos, originais) {
  const antes = new Map((originais || []).map((e) => [e.setorId, e.forma]));
  return (elementos || [])
    .filter((e) => antes.has(e.setorId) && JSON.stringify(antes.get(e.setorId)) !== JSON.stringify(e.forma))
    .map((e) => ({ alvo: "setor", id: e.setorId, forma: e.forma, versao: e.versao }));
}
